import {
  BehaviorSubject,
  catchError,
  combineLatest,
  combineLatestWith,
  filter,
  merge,
  Observable,
  of,
  Subject,
  takeUntil,
  tap,
} from 'rxjs';

import { SlApiService } from '../sl-api.service';
import { SlDataService } from '../sl-data.service';
import {
  shouldShowTranslationWarningLSKey,
  SlLEContext,
  COURSE_CATEGORY_TITLE_KEYS,
} from '../../global-constants';
import { SlActions } from '../ActionsService/sl-actions';
import { SlNavigationService } from '../sl-navigation.service';
import {
  ActionType,
  CurrentPageType,
  ICourseCurrentItems,
  RequestType,
} from '../../global-interface';
import { getInitialServerSideData } from '../../../../shared/public/SlUtils/getInitialServerSideData';
import { IError, ErrorCodes } from '../../../../api/private/global.interface';
import {
  IMaterial, LocationType, ILearningPathSubtree, MaterialRequirementType, MaterialType, VisibilityStatus, ICategorizedCourse,
} from '../../../../api/private/learn-engine-api/learn-engine-api.interface';
import { ICertificate } from '../../../../api/public/certificates-api';
import { SlErrorHandlerService } from '../../../../api/public/error-handling';
import {
  Service, Container, DataService, AuthService, StorageService, ILearningExperience, EnrollmentStatus, LearningExperienceType, i18n, history, CourseCategory,
  UserTrackService,
  CourseTrack,
  SubscriptionPlanService,
} from '../../../../symphony';

@Service()
export class SlCourseService {
  private api = Container.take(SlLEContext, SlApiService);

  private dataService = Container.take(SlLEContext, SlDataService);

  private actions$ = Container.take(SlLEContext, SlActions).actions$;

  public globalData = Container.take('global', DataService);

  public authService = Container.take('global', AuthService);

  public storageService = Container.take('global', StorageService);

  public userTrackService = Container.take('global', UserTrackService);

  private subscriptionPlanService = Container.take('global', SubscriptionPlanService);

  private navigationService = Container.take(
    SlLEContext,
    SlNavigationService,
  );

  private onOnboardingCourseReady$ = new BehaviorSubject<IMaterial>(null);

  constructor() {
    const unsubscriber: Subject<void> = Container.take(SlLEContext, 'unsubscriber') || new Subject<void>();

    this.subscriptionPlanService.subscriptionPlanConfigs
      .pipe(
        filter(c => !!c),
        takeUntil(unsubscriber)
      ).subscribe(() => {
        if (this.dataService?.course$?.value) {
          const alias = this.dataService.course$.value.materialInfo.header.alias;
          const sub = this.getCourseSubtree(alias).subscribe(() => {
            sub.unsubscribe();
          });
        }
      });

    merge(
      this.dataService.justStatusUpdatedCourse$.pipe(
        filter((c) => !!c),
        tap(this.handleJustUpdatedCourse),
      ),
      this.dataService.alias$.pipe(
        filter((a) => !!a),
        combineLatestWith(this.globalData.learningExperiences$.pipe(
          filter((le) => !!le),
        )),
        tap(([alias, le]) => this.globalData.learningExperience$.next(le.find((val) => val.alias === alias))),
      ),
      combineLatest([
        this.onOnboardingCourseReady$.pipe(filter((c) => !!c)),
        this.globalData.learningExperience$.pipe(filter((le) => !!le)),
      ]).pipe(
        filter(([course, le]) => course.materialInfo.header.alias === le.alias),
        tap(([course, le]) => {
          this.actions$.next({
            type: ActionType.OnOnboardingCourseReady,
            requestType: RequestType.Parallel,
            data: {
              material: course,
              learningExperience: le,
            },
          });
        }),
      ),
    )
      .pipe(takeUntil(unsubscriber))
      .subscribe();
  }

  private handleError = Container.take(SlLEContext, SlErrorHandlerService)
    .handleError;

  public getCourse = (alias: string): Observable<null> => {
    const { course$ } = this.dataService;
    const { learningExperiences$ } = this.globalData;

    if (course$.value) {
      if (course$.value.materialInfo.header.alias === alias) {
        this.setCourseCurrentItems(course$.value);
        return of(null);
      }
      course$.next(null);
    }

    const courseData: ILearningExperience = learningExperiences$.value
      && learningExperiences$.value.find((item) => item.alias === alias);

    if (this.authService.isLoggedIn()) {
      this.actions$.next({
        type:
          courseData
            && (courseData.enrollmentStatusId === EnrollmentStatus.None
              || courseData.enrollmentStatusId
              === EnrollmentStatus.Removed)
            ? ActionType.EnrollCourse
            : ActionType.GetCourseSubtree,
        requestType: RequestType.Parallel,
        data: {
          alias,
          type: LearningExperienceType.Course,
        },
      });
    }

    return of(null);
  };

  public getLearningPath = (alias: string): Observable<null> => {
    const { learningPath$, course$ } = this.dataService;
    const { learningExperiences$ } = this.globalData;

    if (learningPath$.value) {
      if (learningPath$.value.alias === alias) {
        const defaultCourseAlias = course$.value
          ? course$.value.materialInfo.header.alias
          : null;
        this.setPathCurrentItems(
          learningPath$.value,
          defaultCourseAlias,
        );
        return of(null);
      }
      learningPath$.next(null);
    }

    const pathData: ILearningExperience = learningExperiences$.value
      && learningExperiences$.value.find((item) => item.alias === alias);

    if (this.authService.isLoggedIn()) {
      this.actions$.next({
        type:
          pathData
            && (pathData.enrollmentStatusId === EnrollmentStatus.None
              || pathData.enrollmentStatusId
              === EnrollmentStatus.Removed)
            ? ActionType.EnrollCourse
            : ActionType.GetLearningPathSubtree,
        requestType: RequestType.Parallel,
        data: {
          alias,
          type: LearningExperienceType.LearningPath,
        },
      });
    }

    return of(null);
  };

  public getCertificate = (alias: string): Observable<ICertificate> => this.api
    .getCertificate(alias)
    .pipe(
      tap((certificate) => this.dataService.certificate$.next(certificate)),
      catchError(err => {
        return of(null);
      })
    );

  private enrollCourseErrorHandler = (
    alias: string,
    type: LearningExperienceType,
    err: IError[],
  ): void => {
    if (!Array.isArray(err)) {
      this.navigationService.navigation$.next({
        toErrorRoute: true,
      });
      return;
    }
    this.handleError(err, {
      [ErrorCodes.BadRequest]: () => {
        this.navigationService.navigation$.next({
          toErrorRoute: true,
        });
      },
      [ErrorCodes.NotFound]: () => {
        this.navigationService.navigation$.next({
          toErrorRoute: true,
        });
      },
      [ErrorCodes.Conflict]: () => {
        this.handleEnrolledCourse(alias, type);
      },
    });
  };

  public enrollCourse = (
    alias: string,
    type: LearningExperienceType,
  ): Observable<null> => {
    const { loading$, routeData$ } = this.dataService;

    const isOngoingFullScreenLoader = loading$.value?.fullScreen;

    if (!isOngoingFullScreenLoader) {
      loading$.next({ courseSkeleton: true });
    }

    return this.api.enrollCourse(alias, routeData$.value.locationUrl).pipe(
      tap(() => {
        this.handleEnrolledCourse(alias, type);
        return of(null);
      }),
      catchError((err) => {
        this.enrollCourseErrorHandler(alias, type, err);
        loading$.next(null);
        return of(null);
      }),
    );
  };

  private handleEnrolledCourse = (
    alias: string,
    type: LearningExperienceType,
  ): void => {
    const { learningExperiences$ } = this.globalData;
    if (learningExperiences$.value) {
      const updatedLearningExperiences = learningExperiences$.value.map(
        (c) => {
          if (c.alias === alias) {
            return {
              ...c,
              enrollmentStatusId: EnrollmentStatus.Enrolled,
              lastActivityDate: new Date().toISOString().slice(0, -5).replace('T', 'T')
            };
          }
          return c;
        },
      );
      learningExperiences$.next(updatedLearningExperiences);
    }

    this.actions$.next({
      type:
        type === LearningExperienceType.LearningPath
          ? ActionType.GetLearningPath
          : ActionType.GetCourse,
      requestType: RequestType.Parallel,
      data: {
        alias,
      },
    });
  };

  public getCourseSubtree = (alias: string): Observable<IMaterial> => {
    const { loading$, routeData$ } = this.dataService;
    const isOngoingFullScreenLoader = loading$.value?.fullScreen;
    if (
      !isOngoingFullScreenLoader
      && routeData$.value?.currentPage === CurrentPageType.CoursePage
    ) {
      loading$.next({ courseSkeleton: true });
    }

    // TO DO - Bad implementation, remove after experiment
    loading$.next({ courseTranslation: true });
    return this.api.getCourseSubTree(alias).pipe(
      tap((courseSubtree) => {
        if (!courseSubtree) {
          if (
            routeData$.value.locationUrl
            === LocationType.LandingPage
          ) {
            this.navigationService.navigation$.next({
              toMainPage: true,
            });
          } else {
            this.navigationService.navigation$.next({
              toNotFoundCourse: true,
            });
          }
          loading$.next(null);
          return of(null);
        }
        this.setCourse(courseSubtree, alias);

        if (routeData$.value.locationUrl === LocationType.Onboarding) {
          this.onOnboardingCourseReady$.next(courseSubtree);
        }

        return of(null);
      }),
      catchError((err) => {
        this.getLrnExpSubtreeErrorHandler(
          err,
          alias,
          LearningExperienceType.Course,
        );
        return of(null);
      }),
    );
  };

  public getLearningPathSubtree = (
    alias: string,
  ): Observable<ILearningPathSubtree> => {
    const { loading$, routeData$ } = this.dataService;
    const isOngoingFullScreenLoader = loading$.value?.fullScreen;
    if (
      !isOngoingFullScreenLoader
      && routeData$.value?.currentPage === CurrentPageType.PathPage
    ) {
      loading$.next({ courseSkeleton: true });
    }

    return this.api.getLearningPathSubTree(alias).pipe(
      tap((pathSubtree) => {
        if (!pathSubtree) {
          if (
            routeData$.value.locationUrl
            === LocationType.LandingPage
          ) {
            this.navigationService.navigation$.next({
              toMainPage: true,
            });
          } else {
            this.navigationService.navigation$.next({
              toNotFoundCourse: true,
            });
          }
          loading$.next(null);
          return of(null);
        }

        this.setLearningPath(pathSubtree);
        loading$.next(null);

        if (routeData$.value.locationUrl === LocationType.Onboarding) {
          this.onOnboardingCourseReady$.next(pathSubtree.units[0].courseSubtree);
        }

        return of(null);
      }),
      catchError((err) => {
        this.getLrnExpSubtreeErrorHandler(
          err,
          alias,
          LearningExperienceType.LearningPath,
        );
        return of(null);
      }),
    );
  };

  private getLrnExpSubtreeErrorHandler = (
    err: IError[],
    alias: string,
    type: LearningExperienceType,
  ): void => {
    const { loading$ } = this.dataService;
    const isOngoingFullScreenLoader = loading$.value?.fullScreen;

    if (!isOngoingFullScreenLoader) {
      loading$.next(null);
    }
    if (!Array.isArray(err)) {
      this.navigationService.navigation$.next({
        toNotFoundCourse: true,
      });
      return;
    }
    this.handleError(err, {
      [ErrorCodes.BadRequest]: () => {
        this.navigationService.navigation$.next({
          toNotFoundCourse: true,
        });
      },
      [ErrorCodes.NotFound]: () => {
        this.navigationService.navigation$.next({
          toNotFoundCourse: true,
        });
      },
      [ErrorCodes.Conflict]: () => {
        history.replace('/learn?type=replaced');
      },
      [ErrorCodes.Locked]: () => {
        this.actions$.next({
          type: ActionType.EnrollCourse,
          requestType: RequestType.Parallel,
          data: {
            alias,
            type,
          },
        });
      },
    });
  };

  private handleJustUpdatedCourse = (course: IMaterial): void => {
    this.setCourseProgress(course);
    this.dataService.course$.next(course);

    const path = this.dataService.learningPath$.value;
    if (path) {
      const unit = path.units.find(
        (u) => u.alias === course.materialInfo.header.alias,
      );
      if (unit) {
        unit.courseSubtree = course;
        this.dataService.learningPath$.next(path);
      }
      this.setPathCurrentItems(path, course.materialInfo.header.alias);
    } else {
      this.setCourseCurrentItems(course);
    }
  };

  public cleanupCourse = (courseAlias: string): Observable<null> => {
    if (
      this.dataService.course$.value?.materialInfo.header.alias
      !== courseAlias
    ) {
      this.dataService.course$.next(null);
    }
    return of(null);
  };

  public cleanupPath = (alias: string): Observable<null> => {
    if (this.dataService.learningPath$.value?.alias !== alias) {
      this.dataService.learningPath$.next(null);
    }
    return of(null);
  };

  public navigateToCourse = (): Observable<null> => {
    this.navigationService.navigation$.next({ toLEPage: true });
    return of(null);
  };

  public scrollIntoCurrentMaterialElement = (
    moduleId: number,
  ): Observable<null> => {
    const { currentItems$, justStatusUpdatedCourse$ } = this.dataService;

    const currentItem = currentItems$.value.find(
      (item) => item.moduleId === moduleId && item.isDefault,
    );
    const currentItemElement = document.querySelector(
      `[le-data-id='${currentItem?.materialId}']`,
    );

    if (currentItemElement) {
      const currentCourseIsJustUpdated = justStatusUpdatedCourse$.value?.materialInfo?.header?.alias
        === currentItem.alias;
      setTimeout(() => {
        currentItemElement.scrollIntoView({
          behavior: 'smooth',
          block: currentCourseIsJustUpdated ? 'center' : 'end',
        });
      }, 100);
    }
    return of(null);
  };

  private setCourse = (course: IMaterial, alias: string): void => {
    const extendedCourse = this.getExtendedCourse(course, alias);
    this.dataService.course$.next(extendedCourse);
    this.userTrackService.setUserTrack(
      this.globalData.learningExperiences$.value?.find(l => l.alias === alias)?.track || this.userTrackService.getUserTrack() || CourseTrack.Codding
    );
    this.setCourseCurrentItems(course);
    this.handleAITranslationWarning();
  };

  private handleAITranslationWarning = async () => {
    const currentLocale = this.dataService.course$.value?.supportedLocales?.find(
      (supportedLocale) => supportedLocale.locale === i18n.language.toUpperCase(),
    );
    const hasSelectedLocale = this.dataService.course$.value?.supportedLocales?.some(
      (locale) => locale.isSelected,
    );

    if (
      !hasSelectedLocale
      && currentLocale
      && !currentLocale.isSelected
      && !currentLocale?.isVerified
    ) {

      this.actions$.next({
        type: ActionType.OnUpdateSelectedLocale,
        requestType: RequestType.Parallel,
        data: {
          locale: currentLocale.locale,
          isFromCourseContainer:
            this.dataService.routeData$.value.locationUrl
            !== LocationType.Onboarding,
        },
      });
      let shouldShowTranslationWarning: Record<string, boolean> = null;
      try {
        shouldShowTranslationWarning = JSON.parse(
          this.storageService.load(
            shouldShowTranslationWarningLSKey,
            'local',
          ),
        );
      } catch (error) {
        console.error(error);
      }
      this.storageService.save(
        shouldShowTranslationWarningLSKey,
        JSON.stringify(
          shouldShowTranslationWarning
            ? {
              ...shouldShowTranslationWarning,
              [this.dataService.course$.value.materialInfo
                .header.id]: true,
            }
            : {
              [this.dataService.course$.value.materialInfo
                .header.id]: true,
            },
        ),
        'local',
      );
    } else {
      this.dataService.loading$.next({ courseTranslation: false });
    }
  };

  private setLearningPath = (path: ILearningPathSubtree): void => {
    this.dataService.learningPath$.next(this.getExtendedPath(path));
    this.setPathCurrentItems(path);
  };

  private getExtendedCourse = (
    course: IMaterial,
    alias: string,
  ): IMaterial => {
    this.setCourseProgress(course);
    this.setCourseHeight(course);
    this.setCourseLanguage(course);
    course.materialInfo.header.alias = alias;
    return course;
  };

  private getExtendedPath = (
    path: ILearningPathSubtree,
  ): ILearningPathSubtree => {
    for (let i = 0; i < path.units.length; ++i) {
      const unit = path.units[i];
      unit.courseSubtree = this.getExtendedCourse(
        unit.courseSubtree,
        unit.alias,
      );
      unit.courseSubtree.materialInfo.header.title = unit.title;
    }
    return path;
  };

  private setCourseLanguage = (course: IMaterial): void => {
    const currentLanguageLocale = course?.supportedLocales?.find(
      (supportedLocale) => supportedLocale.locale.toLowerCase() === i18n.language,
    );

    course.materialInfo.defaultLanguage = currentLanguageLocale?.isVerified
      ? currentLanguageLocale.locale
      : 'EN';
  };

  private setCourseProgress = (course: IMaterial): void => {
    const allRequiredMaterials: IMaterial[] = [];

    course.children.forEach((module) => {
      if (module.children?.length) {
        allRequiredMaterials.push(
          ...module.children.filter(
            ({ materialInfo: { requirementTypeId } }) => requirementTypeId
              !== MaterialRequirementType.Optional,
          ),
        );
      }
    });

    const lastCompletedMaterialIndex = this.getLastCompletedIndex(allRequiredMaterials);

    course.materialInfo.courseProgress = {
      allRequiredMaterialsLength: allRequiredMaterials.length,
      lastCompletedMaterialIndex,
    };
  };

  private setCourseHeight = (course: IMaterial): void => {
    let courseHeight = course.children.length * 77;
    const currentModule = this.getCurrentChild(course);

    for (let i = 0; i < course.children.length; ++i) {
      const module = course.children[i];
      const isCurrentModule = currentModule?.materialInfo?.header?.id
        === module.materialInfo.header.id;
      const childrenHeight = 133 * (module.children?.length || 0)
        + (isCurrentModule ? 64 : 0);
      module.materialInfo.childrenHeight = childrenHeight;
      courseHeight += childrenHeight;
    }

    course.materialInfo.childrenHeight = courseHeight;
  };

  public setCourseCurrentItems = (course: IMaterial): Observable<null> => {
    const courseCurrentItems = this.getCourseCurrentItems(course, true);
    if (courseCurrentItems) {
      this.dataService.currentItems$.next([courseCurrentItems]);
    }
    return of(null);
  };

  public setPathCurrentItems = (
    path: ILearningPathSubtree,
    defaultCourseAlias: string = null,
  ): Observable<null> => {
    const currentItems: ICourseCurrentItems[] = [];
    path.units.forEach(({ courseSubtree }, index) => {
      const courseCurrentItems = this.getCourseCurrentItems(
        courseSubtree,
        defaultCourseAlias
          ? courseSubtree.materialInfo.header.alias
          === defaultCourseAlias
          : index === 0,
      );
      if (courseCurrentItems) {
        currentItems.push(courseCurrentItems);
      }
    });
    this.dataService.currentItems$.next(currentItems);
    return of(null);
  };

  private getCourseCurrentItems = (
    course: IMaterial,
    isDefault: boolean,
  ): ICourseCurrentItems => {
    if (!course) {
      return null;
    }

    const currentModule = this.getCurrentChild(course);
    if (
      !currentModule
      || currentModule.materialInfo.header.typeId !== MaterialType.Module
    ) {
      return null;
    }

    const currentMaterial = this.getCurrentChild(currentModule);

    return {
      alias: course.materialInfo.header.alias,
      moduleId: currentModule.materialInfo.header.materialRelationId,
      materialId: currentMaterial.materialInfo.header.materialRelationId,
      isDefault,
    };
  };

  private getCurrentChild = (mat: IMaterial): IMaterial => {
    const lastCompletedChildIndex = this.getLastCompletedIndex(
      mat.children,
    );
    let currentChild = mat.children?.find(
      (m, i) => i > lastCompletedChildIndex
        && m.materialInfo.status.visibility
        === VisibilityStatus.Unlocked
        && m.materialInfo.requirementTypeId
        !== MaterialRequirementType.Optional,
    );
    if (!currentChild) {
      currentChild = mat.children?.find(
        (m) => m.materialInfo.status.visibility
          === VisibilityStatus.Unlocked
          && m.materialInfo.requirementTypeId
          !== MaterialRequirementType.Optional
          && !m.materialInfo.status.isCompleted,
      );
    }
    return currentChild;
  };

  private getLastCompletedIndex = (materials: IMaterial[]): number => {
    let lastCompletedChildIndex = -1;

    if (materials.length > 0) {
      for (let i = materials.length - 1; i >= 0; --i) {
        if (materials[i].materialInfo.status?.isCompleted) {
          lastCompletedChildIndex = i;
          break;
        }
      }
    }

    return lastCompletedChildIndex;
  };

  public getCourseCategories = (): Observable<ICategorizedCourse[]> => {
    const { loading$, courseCategories$ } = this.dataService;

    if (courseCategories$.value) {
      return of(courseCategories$.value);
    }

    const ssrCourseCategories = getInitialServerSideData(
      'ssrCourseCategories',
    );

    if (ssrCourseCategories) {
      courseCategories$.next(this.getFinalCategories(ssrCourseCategories));
      return of(null);
    }

    loading$.next({ getCourseCategories: true });

    return this.api.getCourseCategories().pipe(
      tap((categories) => {
        courseCategories$.next(this.getFinalCategories(categories));
        loading$.next({
          ...loading$.value,
          getCourseCategories: false,
        });
      }),
      catchError(() => {
        loading$.next({
          ...loading$.value,
          getCourseCategories: false,
        });
        return of(null);
      }),
    );
  };

  private getFinalCategories = (categories: ICategorizedCourse[]): ICategorizedCourse[] => {
    categories.unshift({
      id: CourseCategory.All,
      name: null,
      orderNumber: 0,
    });
    categories?.forEach((c) => {
      c.name = `le-category.title.${COURSE_CATEGORY_TITLE_KEYS[c.id]}`;
    });
    return categories.sort((a, b) => a.orderNumber - b.orderNumber);
  };
}
